import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { DiscountApi } from "../../api/DiscountApi";
import type { CatalogDiscountCategoryFetchOptions } from "../../generated/dto/CatalogDiscountCategoryFetchOptions";
import type { CatalogDiscountCategoryStaff } from "../../generated/dto/CatalogDiscountCategoryStaff";
import type { CatalogDiscountFetchOptions } from "../../generated/dto/CatalogDiscountFetchOptions";
import type { CatalogDiscountInstance } from "../../generated/dto/CatalogDiscountInstance";
import type { CatalogDiscountInstanceFetchOptions } from "../../generated/dto/CatalogDiscountInstanceFetchOptions";
import type { CatalogDiscountInstanceStaff } from "../../generated/dto/CatalogDiscountInstanceStaff";
import type { CatalogDiscountSearchParams } from "../../generated/dto/CatalogDiscountSearchParams";
import type { CatalogDiscountStaff } from "../../generated/dto/CatalogDiscountStaff";
import type { CatalogDiscountStaffSearchResult } from "../../generated/dto/CatalogDiscountStaffSearchResult";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemDiscountFetchOptions } from "../../generated/dto/CatalogItemDiscountFetchOptions";
import type { CatalogItemDiscountStaff } from "../../generated/dto/CatalogItemDiscountStaff";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CreateCatalogDiscount } from "../../generated/dto/CreateCatalogDiscount";
import type { CreateCatalogDiscountCategory } from "../../generated/dto/CreateCatalogDiscountCategory";
import type { CreateCatalogDiscountInstance } from "../../generated/dto/CreateCatalogDiscountInstance";
import type { CreateCatalogItemDiscount } from "../../generated/dto/CreateCatalogItemDiscount";
import type { DeleteCatalogDiscountCategoryStaff } from "../../generated/dto/DeleteCatalogDiscountCategoryStaff";
import type { DeleteCatalogDiscountInstanceStaff } from "../../generated/dto/DeleteCatalogDiscountInstanceStaff";
import type { DeleteCatalogDiscountStaff } from "../../generated/dto/DeleteCatalogDiscountStaff";
import type { DeleteCatalogItemDiscountStaff } from "../../generated/dto/DeleteCatalogItemDiscountStaff";
import type { DiscountVersionChangeRequest } from "../../generated/dto/DiscountVersionChangeRequest";
import type { UpdateCatalogDiscount } from "../../generated/dto/UpdateCatalogDiscount";
import type { UpdateCatalogDiscountCategory } from "../../generated/dto/UpdateCatalogDiscountCategory";
import type { UpdateCatalogDiscountInstance } from "../../generated/dto/UpdateCatalogDiscountInstance";
import type { UpdateCatalogItemDiscount } from "../../generated/dto/UpdateCatalogItemDiscount";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class DiscountApiClient implements DiscountApi {
  constructor(private readonly client = useClient()) {}

  async getCatalogDiscountCategoryStaff(id: number, options?: CatalogDiscountCategoryFetchOptions): Promise<CatalogDiscountCategoryStaff> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountcategory/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountCategoryFetchOptions") });
    return deserializeCbiDto<CatalogDiscountCategoryStaff>(data, "generated.dto.CatalogDiscountCategoryStaff");
  }

  async getCatalogItemDiscountStaff(id: number, options?: CatalogItemDiscountFetchOptions): Promise<CatalogItemDiscountStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemdiscount/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemDiscountFetchOptions") });
    return deserializeCbiDto<CatalogItemDiscountStaff>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async getCatalogDiscountStaff(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    const { data } = await this.client.get<unknown>(`/catalogdiscount/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<CatalogDiscountStaff>(data, "generated.dto.CatalogDiscountStaff");
  }

  async getCatalogDiscountInstanceStaff(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<CatalogDiscountInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountinstance/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountInstanceFetchOptions") });
    return deserializeCbiDto<CatalogDiscountInstanceStaff>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async getAllCatalogDiscountCategoryStaffs(options?: CatalogDiscountCategoryFetchOptions): Promise<Array<CatalogDiscountCategoryStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountcategory/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountCategoryFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountCategoryStaff>>(data, "generated.dto.CatalogDiscountCategoryStaff");
  }

  async getAllCatalogItemDiscountStaffs(options?: CatalogItemDiscountFetchOptions): Promise<Array<CatalogItemDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitemdiscount/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogItemDiscountFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemDiscountStaff>>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async getAllCatalogDiscountStaffs(options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogdiscount/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountStaff>>(data, "generated.dto.CatalogDiscountStaff");
  }

  async getAllCatalogDiscountInstanceStaffs(options?: CatalogDiscountInstanceFetchOptions): Promise<Array<CatalogDiscountInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountinstance/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountInstanceFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountInstanceStaff>>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async createCatalogDiscountCategoryStaff(source: CreateCatalogDiscountCategory): Promise<CatalogDiscountCategoryStaff> {
    const { data } = await this.client.post<unknown>(`/catalogdiscountcategory/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogDiscountCategory"));
    return deserializeCbiDto<CatalogDiscountCategoryStaff>(data, "generated.dto.CatalogDiscountCategoryStaff");
  }

  async createCatalogItemDiscountStaff(source: CreateCatalogItemDiscount): Promise<CatalogItemDiscountStaff> {
    const { data } = await this.client.post<unknown>(`/catalogitemdiscount/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemDiscount"));
    return deserializeCbiDto<CatalogItemDiscountStaff>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async createCatalogDiscountStaff(source: CreateCatalogDiscount): Promise<CatalogDiscountStaff> {
    const { data } = await this.client.post<unknown>(`/catalogdiscount/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogDiscount"));
    return deserializeCbiDto<CatalogDiscountStaff>(data, "generated.dto.CatalogDiscountStaff");
  }

  async createCatalogDiscountInstanceStaff(source: CreateCatalogDiscountInstance): Promise<CatalogDiscountInstanceStaff> {
    const { data } = await this.client.post<unknown>(`/catalogdiscountinstance/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogDiscountInstance"));
    return deserializeCbiDto<CatalogDiscountInstanceStaff>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async updateCatalogDiscountCategoryStaff(source: UpdateCatalogDiscountCategory): Promise<CatalogDiscountCategoryStaff> {
    const { data } = await this.client.put<unknown>(`/catalogdiscountcategory/staff/${source.categoryId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogDiscountCategory"));
    return deserializeCbiDto<CatalogDiscountCategoryStaff>(data, "generated.dto.CatalogDiscountCategoryStaff");
  }

  async updateCatalogItemDiscountStaff(source: UpdateCatalogItemDiscount): Promise<CatalogItemDiscountStaff> {
    const { data } = await this.client.put<unknown>(`/catalogitemdiscount/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemDiscount"));
    return deserializeCbiDto<CatalogItemDiscountStaff>(data, "generated.dto.CatalogItemDiscountStaff");
  }

  async updateCatalogDiscountStaff(source: UpdateCatalogDiscount): Promise<CatalogDiscountStaff> {
    const { data } = await this.client.put<unknown>(`/catalogdiscount/staff/${source.discountId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogDiscount"));
    return deserializeCbiDto<CatalogDiscountStaff>(data, "generated.dto.CatalogDiscountStaff");
  }

  async updateCatalogDiscountInstanceStaff(source: UpdateCatalogDiscountInstance): Promise<CatalogDiscountInstanceStaff> {
    const { data } = await this.client.put<unknown>(`/catalogdiscountinstance/staff/${source.instanceId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogDiscountInstance"));
    return deserializeCbiDto<CatalogDiscountInstanceStaff>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async deleteCatalogDiscountCategoryStaff(source: DeleteCatalogDiscountCategoryStaff): Promise<void> {
    await this.client.delete(`/catalogdiscountcategory/staff/${source.categoryId}`);
  }

  async deleteCatalogItemDiscountStaff(source: DeleteCatalogItemDiscountStaff): Promise<void> {
    await this.client.delete(`/catalogitemdiscount/staff/${source.assignId}`);
  }

  async deleteCatalogDiscountStaff(source: DeleteCatalogDiscountStaff): Promise<void> {
    await this.client.delete(`/catalogdiscount/staff/${source.discountId}`);
  }

  async deleteCatalogDiscountInstanceStaff(source: DeleteCatalogDiscountInstanceStaff): Promise<void> {
    await this.client.delete(`/catalogdiscountinstance/staff/${source.instanceId}`);
  }

  async searchCatalogDiscountStaff(params: CatalogDiscountSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogDiscountStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/catalogdiscount/staff/search`, serializeCbiDto(params, "generated.dto.CatalogDiscountSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CatalogDiscountStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogDiscountStaffSearchResult>>(result.items, "generated.dto.CatalogDiscountStaffSearchResult") };
    return result;
  }

  async searchCatalogDiscountStaffFull(params: CatalogDiscountSearchParams, page: number, perPage: number, options?: CatalogDiscountFetchOptions): Promise<PaginationResult<CatalogDiscountStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogdiscount/staff/search/full`, serializeCbiDto(params, "generated.dto.CatalogDiscountSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CatalogDiscountStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogDiscountStaff>>(result.items, "generated.dto.CatalogDiscountStaff") };
    return result;
  }

  async getCatalogDiscountCategoryStaffDiscounts(id: number, options?: CatalogDiscountFetchOptions): Promise<Array<CatalogDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountcategory/staff/${id}/discounts`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountStaff>>(data, "generated.dto.CatalogDiscountStaff");
  }

  async getCatalogItemDiscountStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemdiscount/staff/${id}/itemid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemDiscountStaffDiscountId(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemdiscount/staff/${id}/discountid`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<CatalogDiscountStaff>(data, "generated.dto.CatalogDiscountStaff");
  }

  async getCatalogDiscountStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff | null> {
    const { data } = await this.client.get<unknown>(`/catalogdiscount/staff/${id}/catalogid`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff | null>(data, "generated.dto.CatalogStaff");
  }

  async getCatalogDiscountStaffCategoryId(id: number, options?: CatalogDiscountCategoryFetchOptions): Promise<CatalogDiscountCategoryStaff | null> {
    const { data } = await this.client.get<unknown>(`/catalogdiscount/staff/${id}/categoryid`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountCategoryFetchOptions") });
    return deserializeCbiDto<CatalogDiscountCategoryStaff | null>(data, "generated.dto.CatalogDiscountCategoryStaff");
  }

  async getCatalogDiscountStaffDiscountInstances(id: number, options?: CatalogDiscountInstanceFetchOptions): Promise<Array<CatalogDiscountInstanceStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogdiscount/staff/${id}/discountinstances`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountInstanceFetchOptions") });
    return deserializeCbiDto<Array<CatalogDiscountInstanceStaff>>(data, "generated.dto.CatalogDiscountInstanceStaff");
  }

  async getCatalogDiscountInstanceStaffDiscountId(id: number, options?: CatalogDiscountFetchOptions): Promise<CatalogDiscountStaff> {
    const { data } = await this.client.get<unknown>(`/catalogdiscountinstance/staff/${id}/discountid`, { params: serializeCbiDto(options, "generated.dto.CatalogDiscountFetchOptions") });
    return deserializeCbiDto<CatalogDiscountStaff>(data, "generated.dto.CatalogDiscountStaff");
  }

  async changeDiscountVersionStaff(request: DiscountVersionChangeRequest): Promise<CatalogDiscountInstance> {
    const { data } = await this.client.post<unknown>(`/discount/staff/changediscountversion`, serializeCbiDto(request, "generated.dto.DiscountVersionChangeRequest"));
    let result = data as CatalogDiscountInstance;
    result = deserializeCbiDto<CatalogDiscountInstance>(result, "generated.dto.CatalogDiscountInstance");
    return result;
  }
}

export const useDiscountApi = (): DiscountApi => new DiscountApiClient();
